import { ECanvasObjectType, WebWoodEngine } from '@renderforest/webwood-engine'

import { TAppDispatch, TGetState } from 'store/types'
import editorSlice from 'store/features/editor/slice'
import projectDataSlice from 'store/features/projectData/slice'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { editorSelectors } from 'store/features/editor/selectors'
import { withAppThunks } from 'store/with-app-thunks'
import { IMediaItem } from 'types/media'

const { actions: editorActions } = editorSlice
const { actions: projectDataActions } = projectDataSlice

/**
 * Builds the `updateMediaItem` payload shared by both `persistEditingText`
 * (mid-edit debounced save) and `commitTextEdit` (final save on blur). The
 * engine is the live source of truth during editing — its `width`/`height`
 * reflect the latest worker render and, for rotated items, its `x`/`y` carry
 * the engine's center-preserving compensation. Fall back to the item's
 * existing values if the engine object isn't around for any reason.
 */
const buildTextItemUpdatePayload = (item: IMediaItem, text: string) => {
  const engine = WebWoodEngine.getInstance()
  const engineObject = engine?.getMediaConfig(
    ECanvasObjectType.text,
    `${item.id}-${item.assetId}`
  )
  return {
    id: item.id,
    updates: {
      width: engineObject?.width ?? item.width,
      height: engineObject?.height ?? item.height,
      coordinates: engineObject
        ? { x: engineObject.x, y: engineObject.y }
        : item.coordinates,
      meta: {
        ...item.meta,
        textData: { ...item.meta.textData!, text }
      }
    }
  }
}

/**
 * Auto-saves the current text mid-edit on each debounced typing pause
 * (~300ms). Uses the undo-tracked `updateMediaItem` so every pause becomes
 * its own undo checkpoint: undo steps back pause-by-pause, and ultimately
 * to the pre-edit text, rather than collapsing the whole session into one
 * step. The undo middleware snapshots the state *before* each pause, so the
 * checkpoint's baseline is the previous pause's text. Redux state also stays
 * current (good for crash recovery + live render + autosave).
 */
const persistEditingText =
  ({ id, text }: { id: string; assetId: string; text: string }) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const editingId = editorSelectors.editingTextId(state)
    if (editingId !== id) return

    // Skip mid-edit persists for empty/whitespace text — the backend rejects
    // empty content, and commitTextEdit will delete the item on blur anyway.
    if (!text.trim()) return

    const item = projectDataSelectors.mediaItems(state).find(m => m.id === id)
    if (!item?.meta?.textData) return
    // No text delta since the last pause — skip so identical-text pauses
    // (e.g. an IME re-flush) don't create a redundant undo checkpoint.
    if (item.meta.textData.text === text) return

    dispatch(
      projectDataActions.updateMediaItem(buildTextItemUpdatePayload(item, text))
    )
  }

// Wrapped in `withAppThunks` so we can dispatch
// `projectDataThunks.removeVisualMediaItem` (cleanup-aware delete) without
// a static cross-feature import — a direct import here creates a cycle
// editor/thunks → projectData/thunks → editor/thunks via the appThunks
// registry. The wrapper resolves the registry lazily at dispatch time.
const commitTextEdit = ({ id, text }: { id: string; text: string }) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const editingId = editorSelectors.editingTextId(state)
    if (editingId !== id) return

    // Empty / whitespace-only commit deletes the text item rather than
    // persisting an invisible-content item the user can't reselect.
    // Route through the thunk (not the raw action) so any asset-cache
    // cleanup the app does on visual-media removal also runs.
    if (!text.trim()) {
      dispatch(editorActions.setEditingTextId(null))
      dispatch(appThunks.projectDataThunks.removeVisualMediaItem(id))
      return
    }

    const item = projectDataSelectors.mediaItems(state).find(m => m.id === id)
    if (item?.meta?.textData && item.meta.textData.text !== text) {
      // Mid-edit pauses already created their own undo checkpoints via
      // persistEditingText. This final commit only records an entry when the
      // text changed since the last pause — i.e. the user typed and blurred
      // within the debounce window, so no pause-persist ran. When a pause
      // already captured the final text this branch is skipped (no-op).
      dispatch(
        projectDataActions.updateMediaItem(
          buildTextItemUpdatePayload(item, text)
        )
      )
    }
    dispatch(editorActions.setEditingTextId(null))
  })

export const textEditThunks = {
  persistEditingText,
  commitTextEdit
}
